/**
 * Microsoft Calendar Authentication Service
 * 
 * Handles OAuth 2.0 authentication flow with Microsoft Graph API
 * for Outlook Calendar and Teams integration.
 * 
 * Note: Microsoft Teams meetings are managed through Outlook Calendar
 * via Microsoft Graph API. This service provides unified access to both.
 * 
 * Security Principles:
 * - OAuth 2.0 with PKCE (no stored credentials)
 * - Tokens held in memory only during session
 * - Least-privilege scopes (calendar read/write, online meetings)
 * - Explicit user consent required
 * - SOC2/GDPR compliant design
 * 
 * @module MicrosoftCalendarAuthService
 */

import type {
  OAuthTokens,
  TokenValidationResult,
  CalendarConnectionState,
  CalendarConnectionError,
  CalendarErrorCode,
  ConnectionResult,
  DisconnectResult,
  AsyncResult,
  ExternalCalendarInfo,
} from './types';
import {
  CALENDAR_SERVER_INTEGRATION_AVAILABLE,
  CALENDAR_SERVER_INTEGRATION_MESSAGE,
} from './calendarSecurityPolicy';

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Microsoft OAuth configuration.
 * Client ID should come from environment variables in production.
 */
interface MicrosoftOAuthConfig {
  clientId: string;
  redirectUri: string;
  scopes: string[];
  tenantId: string;
}

/**
 * Default OAuth scopes for Microsoft Calendar/Teams integration.
 * Using minimum required scopes for calendar and online meetings access.
 * 
 * Scopes:
 * - Calendars.ReadWrite: Read and create calendar events
 * - OnlineMeetings.ReadWrite: Create Teams meetings
 * - User.Read: Get user profile info
 * - offline_access: Receive refresh tokens
 */
const DEFAULT_SCOPES = [
  'Calendars.ReadWrite',
  'OnlineMeetings.ReadWrite',
  'User.Read',
  'offline_access',
];

/**
 * Token expiration buffer in seconds.
 * Refresh tokens before they expire by this amount.
 */
const TOKEN_EXPIRY_BUFFER_SECONDS = 300; // 5 minutes

/**
 * Microsoft OAuth endpoints.
 * Using 'common' tenant for multi-tenant app support.
 */
const getMicrosoftOAuthEndpoints = (tenantId: string) => ({
  authorization: `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`,
  token: `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`,
  logout: `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout`,
  userInfo: 'https://graph.microsoft.com/v1.0/me',
  calendarList: 'https://graph.microsoft.com/v1.0/me/calendars',
} as const);

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a structured error object.
 */
function createError(
  code: CalendarErrorCode,
  message: string,
  details?: string,
  recoverable = true,
  suggestedAction?: string
): CalendarConnectionError {
  return {
    code,
    message,
    details,
    recoverable,
    suggestedAction,
    occurredAt: new Date().toISOString(),
  };
}

/**
 * Generates a cryptographically secure random string for OAuth state parameter.
 */
function generateStateParam(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Generates PKCE code verifier and challenge for secure OAuth flow.
 * Microsoft requires PKCE for single-page applications.
 */
async function generatePKCE(): Promise<{ verifier: string; challenge: string }> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const verifier = btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = new Uint8Array(hashBuffer);
  const challenge = btoa(String.fromCharCode(...hashArray))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

  return { verifier, challenge };
}

// ─────────────────────────────────────────────────────────────────────────────
// Microsoft Calendar Auth Service Class
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Service for managing Microsoft Calendar/Teams OAuth authentication.
 * 
 * Usage:
 * ```typescript
 * const authService = MicrosoftCalendarAuthService.getInstance();
 * const result = await authService.connect();
 * if (result.success) {
 *   console.log('Connected:', result.data.state.userEmail);
 * }
 * ```
 */
export class MicrosoftCalendarAuthService {
  private static instance: MicrosoftCalendarAuthService | null = null;

  // In-memory token storage (never persisted to disk/localStorage for security)
  private tokens: OAuthTokens | null = null;

  // Current connection state
  private connectionState: CalendarConnectionState = {
    status: 'disconnected',
    provider: 'microsoft',
  };

  // OAuth flow state (for popup callback verification)
  private pendingAuthState: string | null = null;
  private pendingCodeVerifier: string | null = null;

  // Configuration
  private config: MicrosoftOAuthConfig;

  // OAuth endpoints (computed from tenant)
  private endpoints: ReturnType<typeof getMicrosoftOAuthEndpoints>;

  // ─────────────────────────────────────────────────────────────────────────
  // Constructor & Singleton
  // ─────────────────────────────────────────────────────────────────────────

  private constructor() {
    // Configuration from environment (with fallbacks for development)
    // Using 'common' tenant allows both personal and work/school accounts
    const tenantId = import.meta.env.VITE_MICROSOFT_TENANT_ID || 'common';

    this.config = {
      clientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID || '',
      redirectUri: import.meta.env.VITE_MICROSOFT_REDIRECT_URI || `${window.location.origin}/auth/microsoft/callback`,
      scopes: DEFAULT_SCOPES,
      tenantId,
    };

    this.endpoints = getMicrosoftOAuthEndpoints(tenantId);
  }

  /**
   * Get singleton instance of the service.
   */
  public static getInstance(): MicrosoftCalendarAuthService {
    if (!MicrosoftCalendarAuthService.instance) {
      MicrosoftCalendarAuthService.instance = new MicrosoftCalendarAuthService();
    }
    return MicrosoftCalendarAuthService.instance;
  }

  /**
   * Reset singleton instance (for testing purposes).
   */
  public static resetInstance(): void {
    if (MicrosoftCalendarAuthService.instance) {
      MicrosoftCalendarAuthService.instance.clearTokens();
      MicrosoftCalendarAuthService.instance = null;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: Connection Management
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Initiates Microsoft Calendar OAuth connection flow.
   * Opens a popup for user authentication.
   * 
   * @returns Connection result with state
   */
  public async connect(): Promise<AsyncResult<ConnectionResult>> {
    if (!CALENDAR_SERVER_INTEGRATION_AVAILABLE) {
      return {
        success: false,
        error: createError(
          'AUTH_FAILED',
          CALENDAR_SERVER_INTEGRATION_MESSAGE,
          'Provider tokens must never be returned to the AX1 browser.',
          false,
          'Contact your administrator after the server-managed integration is deployed',
        ),
      };
    }

    try {
      // Validate configuration
      if (!this.config.clientId) {
        return {
          success: false,
          error: createError(
            'AUTH_FAILED',
            'Microsoft OAuth not configured',
            'Missing VITE_MICROSOFT_CLIENT_ID environment variable',
            false,
            'Contact your administrator to configure Microsoft Calendar integration'
          ),
        };
      }

      // Update state to connecting
      this.updateConnectionState({ status: 'connecting' });

      // Generate PKCE and state for secure OAuth flow
      const { verifier, challenge } = await generatePKCE();
      const state = generateStateParam();

      this.pendingAuthState = state;
      this.pendingCodeVerifier = verifier;

      // Build authorization URL
      const authUrl = this.buildAuthorizationUrl(state, challenge);

      // Open popup for OAuth flow
      const authResult = await this.openAuthPopup(authUrl);

      if (!authResult.success) {
        this.updateConnectionState({
          status: 'error',
          error: authResult.error,
        });
        return authResult as AsyncResult<ConnectionResult>;
      }

      // Exchange authorization code for tokens
      const tokenResult = await this.exchangeCodeForTokens(authResult.data.code);

      if (!tokenResult.success) {
        this.updateConnectionState({
          status: 'error',
          error: tokenResult.error,
        });
        return {
          success: false,
          error: tokenResult.error,
        };
      }

      // Store tokens in memory
      this.tokens = tokenResult.data;

      // Fetch user info and calendar list
      const userInfoResult = await this.fetchUserInfo();
      const calendarsResult = await this.fetchCalendarList();

      // Update connection state with full info
      this.updateConnectionState({
        status: 'connected',
        userEmail: userInfoResult.success ? userInfoResult.data.email : undefined,
        userName: userInfoResult.success ? userInfoResult.data.name : undefined,
        connectedAt: new Date().toISOString(),
        lastTokenRefresh: new Date().toISOString(),
        availableCalendars: calendarsResult.success ? calendarsResult.data : [],
        error: undefined,
      });

      return {
        success: true,
        data: {
          connected: true,
          state: this.getConnectionState(),
        },
      };
    } catch (error) {
      const connectionError = createError(
        'UNKNOWN_ERROR',
        'Failed to connect to Microsoft Calendar',
        error instanceof Error ? error.message : String(error),
        true,
        'Please try again'
      );

      this.updateConnectionState({
        status: 'error',
        error: connectionError,
      });

      return {
        success: false,
        error: connectionError,
      };
    } finally {
      // Clear pending auth state
      this.pendingAuthState = null;
      this.pendingCodeVerifier = null;
    }
  }

  /**
   * Disconnects from Microsoft Calendar and clears tokens.
   * Note: Microsoft doesn't have a token revocation endpoint like Google.
   * We clear local state and optionally redirect to logout.
   * 
   * @param performLogout - Whether to redirect to Microsoft logout (default: false)
   * @returns Disconnect result
   */
  public async disconnect(performLogout = false): Promise<AsyncResult<DisconnectResult>> {
    try {
      const warnings: string[] = [];

      // Clear local state
      this.clearTokens();
      this.updateConnectionState({
        status: 'disconnected',
        userEmail: undefined,
        userName: undefined,
        connectedAt: undefined,
        lastTokenRefresh: undefined,
        availableCalendars: undefined,
        error: undefined,
      });

      // Optionally perform full Microsoft logout
      if (performLogout) {
        // Note: This will redirect the user, so only use when explicitly requested
        warnings.push('Microsoft logout will redirect you to Microsoft sign-out page');
        // We don't actually redirect here - that would be a side effect
        // Instead, we return the logout URL for the caller to use if needed
      }

      return {
        success: true,
        data: {
          disconnected: true,
          tokensRevoked: false, // Microsoft doesn't support token revocation via API
          warnings: [
            'Microsoft tokens will expire naturally. For full sign-out, visit Microsoft account settings.',
            ...warnings,
          ],
        },
      };
    } catch (error) {
      // Even on error, clear local state for security
      this.clearTokens();
      this.updateConnectionState({ status: 'disconnected' });

      return {
        success: false,
        error: createError(
          'UNKNOWN_ERROR',
          'Error during disconnect',
          error instanceof Error ? error.message : String(error),
          true
        ),
      };
    }
  }

  /**
   * Gets the Microsoft logout URL for full sign-out.
   * Use this when user wants to completely sign out of Microsoft.
   */
  public getLogoutUrl(): string {
    const params = new URLSearchParams({
      post_logout_redirect_uri: window.location.origin,
    });
    return `${this.endpoints.logout}?${params.toString()}`;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: Token Management
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Validates the current access token.
   * 
   * @returns Token validation result
   */
  public validateToken(): TokenValidationResult {
    if (!this.tokens) {
      return {
        isValid: false,
        isExpired: true,
        isExpiringSoon: false,
        secondsUntilExpiry: 0,
        error: 'No tokens available',
      };
    }

    const now = Math.floor(Date.now() / 1000);
    const secondsUntilExpiry = this.tokens.expiresAt - now;
    const isExpired = secondsUntilExpiry <= 0;
    const isExpiringSoon = secondsUntilExpiry <= TOKEN_EXPIRY_BUFFER_SECONDS;

    return {
      isValid: !isExpired,
      isExpired,
      isExpiringSoon,
      secondsUntilExpiry,
    };
  }

  /**
   * Gets a valid access token, refreshing if necessary.
   * 
   * @returns Valid access token or error
   */
  public async getValidAccessToken(): Promise<AsyncResult<string>> {
    if (!CALENDAR_SERVER_INTEGRATION_AVAILABLE) {
      return {
        success: false,
        error: createError(
          'AUTH_FAILED',
          CALENDAR_SERVER_INTEGRATION_MESSAGE,
          'Browser access to provider tokens is disabled.',
          false,
        ),
      };
    }

    const validation = this.validateToken();

    if (validation.isExpired) {
      // Attempt to refresh
      const refreshResult = await this.refreshAccessToken();
      if (!refreshResult.success) {
        this.updateConnectionState({
          status: 'token_expired',
          error: refreshResult.error,
        });
        return refreshResult;
      }
    } else if (validation.isExpiringSoon && this.tokens?.refreshToken) {
      // Proactively refresh token that's expiring soon
      // Don't await - let it happen in background
      this.refreshAccessToken().catch(console.error);
    }

    if (!this.tokens?.accessToken) {
      return {
        success: false,
        error: createError(
          'TOKEN_EXPIRED',
          'No valid access token available',
          undefined,
          true,
          'Please reconnect your Microsoft Calendar'
        ),
      };
    }

    return {
      success: true,
      data: this.tokens.accessToken,
    };
  }

  /**
   * Refreshes the access token using the refresh token.
   * 
   * @returns New access token or error
   */
  public async refreshAccessToken(): Promise<AsyncResult<string>> {
    if (!this.tokens?.refreshToken) {
      return {
        success: false,
        error: createError(
          'TOKEN_REFRESH_FAILED',
          'No refresh token available',
          'User must re-authenticate',
          true,
          'Please reconnect your Microsoft Calendar'
        ),
      };
    }

    try {
      // In production, this would call our backend to refresh tokens
      // The backend handles the client_secret securely (if using confidential client)
      const response = await fetch('/api/auth/microsoft/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: this.tokens.refreshToken,
        }),
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const data = await response.json();

      // Update stored tokens (Microsoft may return a new refresh token)
      this.tokens = {
        accessToken: data.access_token,
        refreshToken: data.refresh_token || this.tokens.refreshToken,
        expiresAt: Math.floor(Date.now() / 1000) + data.expires_in,
        tokenType: data.token_type || 'Bearer',
        scopes: (data.scope || '').split(' ').filter(Boolean),
      };

      this.updateConnectionState({
        lastTokenRefresh: new Date().toISOString(),
        status: 'connected',
        error: undefined,
      });

      return {
        success: true,
        data: this.tokens.accessToken,
      };
    } catch (error) {
      const refreshError = createError(
        'TOKEN_REFRESH_FAILED',
        'Failed to refresh access token',
        error instanceof Error ? error.message : String(error),
        true,
        'Please reconnect your Microsoft Calendar'
      );

      return {
        success: false,
        error: refreshError,
      };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: State Access
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Gets the current connection state.
   */
  public getConnectionState(): CalendarConnectionState {
    return { ...this.connectionState };
  }

  /**
   * Checks if currently connected to Microsoft Calendar.
   */
  public isConnected(): boolean {
    return CALENDAR_SERVER_INTEGRATION_AVAILABLE
      && this.connectionState.status === 'connected'
      && this.tokens !== null;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private Methods: OAuth Flow
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Builds the Microsoft OAuth authorization URL.
   */
  private buildAuthorizationUrl(state: string, codeChallenge: string): string {
    const params = new URLSearchParams({
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      response_type: 'code',
      scope: this.config.scopes.join(' '),
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      response_mode: 'query',
      prompt: 'consent', // Always show consent screen for refresh token
    });

    return `${this.endpoints.authorization}?${params.toString()}`;
  }

  /**
   * Opens OAuth popup and waits for callback.
   */
  private async openAuthPopup(
    authUrl: string
  ): Promise<AsyncResult<{ code: string }>> {
    return new Promise((resolve) => {
      // Popup dimensions and position
      const width = 500;
      const height = 700; // Microsoft consent screen is taller
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        'microsoft-oauth',
        `width=${width},height=${height},left=${left},top=${top},popup=1`
      );

      if (!popup) {
        resolve({
          success: false,
          error: createError(
            'POPUP_BLOCKED',
            'Popup was blocked by the browser',
            'Please allow popups for this site and try again',
            true,
            'Enable popups in your browser settings'
          ),
        });
        return;
      }

      // Listen for callback message from popup
      const handleMessage = (event: MessageEvent) => {
        // Verify origin
        if (event.origin !== window.location.origin) return;

        const { type, code, state, error, errorDescription } = event.data || {};

        if (type !== 'microsoft-oauth-callback') return;

        // Cleanup
        window.removeEventListener('message', handleMessage);
        clearInterval(pollTimer);
        popup.close();

        // Verify state parameter
        if (state !== this.pendingAuthState) {
          resolve({
            success: false,
            error: createError(
              'AUTH_FAILED',
              'OAuth state mismatch',
              'Security verification failed. Please try again.',
              true
            ),
          });
          return;
        }

        if (error) {
          const errorCode: CalendarErrorCode =
            error === 'access_denied' ? 'USER_CANCELLED' : 'AUTH_FAILED';
          resolve({
            success: false,
            error: createError(
              errorCode,
              error === 'access_denied'
                ? 'User cancelled the authorization'
                : 'Authorization failed',
              errorDescription || error,
              errorCode === 'USER_CANCELLED'
            ),
          });
          return;
        }

        if (code) {
          resolve({
            success: true,
            data: { code },
          });
        }
      };

      window.addEventListener('message', handleMessage);

      // Poll for popup closure (user closed without completing)
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer);
          window.removeEventListener('message', handleMessage);
          resolve({
            success: false,
            error: createError(
              'USER_CANCELLED',
              'Authorization window was closed',
              undefined,
              true
            ),
          });
        }
      }, 500);
    });
  }

  /**
   * Exchanges authorization code for tokens via backend.
   */
  private async exchangeCodeForTokens(
    code: string
  ): Promise<AsyncResult<OAuthTokens>> {
    try {
      // Call backend to exchange code
      // For public clients (SPA), we can exchange directly but it's more secure via backend
      const response = await fetch('/api/auth/microsoft/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          codeVerifier: this.pendingCodeVerifier,
          redirectUri: this.config.redirectUri,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Token exchange failed: ${response.status}`);
      }

      const data = await response.json();

      const tokens: OAuthTokens = {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: Math.floor(Date.now() / 1000) + data.expires_in,
        tokenType: data.token_type || 'Bearer',
        scopes: (data.scope || '').split(' ').filter(Boolean),
      };

      return {
        success: true,
        data: tokens,
      };
    } catch (error) {
      return {
        success: false,
        error: createError(
          'AUTH_FAILED',
          'Failed to exchange authorization code',
          error instanceof Error ? error.message : String(error),
          true,
          'Please try again'
        ),
      };
    }
  }

  /**
   * Fetches user info from Microsoft Graph.
   */
  private async fetchUserInfo(): Promise<
    AsyncResult<{ email: string; name: string }>
  > {
    if (!this.tokens?.accessToken) {
      return {
        success: false,
        error: createError('AUTH_FAILED', 'No access token available'),
      };
    }

    try {
      const response = await fetch(this.endpoints.userInfo, {
        headers: {
          Authorization: `Bearer ${this.tokens.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user info: ${response.status}`);
      }

      const data = await response.json();

      return {
        success: true,
        data: {
          email: data.mail || data.userPrincipalName,
          name: data.displayName || data.mail || data.userPrincipalName,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Failed to fetch user info',
          error instanceof Error ? error.message : String(error)
        ),
      };
    }
  }

  /**
   * Fetches list of calendars from Microsoft Graph.
   */
  private async fetchCalendarList(): Promise<AsyncResult<ExternalCalendarInfo[]>> {
    if (!this.tokens?.accessToken) {
      return {
        success: false,
        error: createError('AUTH_FAILED', 'No access token available'),
      };
    }

    try {
      const response = await fetch(this.endpoints.calendarList, {
        headers: {
          Authorization: `Bearer ${this.tokens.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch calendars: ${response.status}`);
      }

      const data = await response.json();

      const calendars: ExternalCalendarInfo[] = (data.value || []).map(
        (cal: Record<string, unknown>) => ({
          id: cal.id as string,
          name: cal.name as string,
          isPrimary: cal.isDefaultCalendar === true,
          color: cal.hexColor as string | undefined,
          // Microsoft Graph returns canEdit, canShare, etc. - map to our access role
          accessRole: cal.canEdit ? 'owner' : 'reader',
          syncEnabled: false, // Default to disabled, user must enable
        })
      );

      return {
        success: true,
        data: calendars,
      };
    } catch (error) {
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Failed to fetch calendar list',
          error instanceof Error ? error.message : String(error)
        ),
      };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private Methods: State Management
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Updates connection state.
   */
  private updateConnectionState(
    updates: Partial<CalendarConnectionState>
  ): void {
    this.connectionState = {
      ...this.connectionState,
      ...updates,
    };
  }

  /**
   * Clears all stored tokens.
   */
  private clearTokens(): void {
    this.tokens = null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Convenience Functions (Facade)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Connects to Microsoft Calendar/Teams.
 * Convenience function that uses the singleton service instance.
 * 
 * @returns Connection result
 */
export async function connectMicrosoftCalendar(): Promise<AsyncResult<ConnectionResult>> {
  const service = MicrosoftCalendarAuthService.getInstance();
  return service.connect();
}

/**
 * Disconnects from Microsoft Calendar/Teams.
 * Convenience function that uses the singleton service instance.
 * 
 * @param performLogout - Whether to redirect to Microsoft logout
 * @returns Disconnect result
 */
export async function disconnectMicrosoftCalendar(performLogout = false): Promise<AsyncResult<DisconnectResult>> {
  const service = MicrosoftCalendarAuthService.getInstance();
  return service.disconnect(performLogout);
}

/**
 * Gets the current Microsoft Calendar connection state.
 * 
 * @returns Current connection state
 */
export function getMicrosoftCalendarConnectionState(): CalendarConnectionState {
  const service = MicrosoftCalendarAuthService.getInstance();
  return service.getConnectionState();
}

/**
 * Checks if connected to Microsoft Calendar.
 * @returns True if connected
 */
export function isMicrosoftCalendarConnected(): boolean {
  if (!CALENDAR_SERVER_INTEGRATION_AVAILABLE) return false;
  const service = MicrosoftCalendarAuthService.getInstance();
  return service.isConnected();
}

/**
 * Gets the Microsoft logout URL for full sign-out.
 * 
 * @returns Logout URL
 */
export function getMicrosoftLogoutUrl(): string {
  const service = MicrosoftCalendarAuthService.getInstance();
  return service.getLogoutUrl();
}

// Default export for the service class
export default MicrosoftCalendarAuthService;
